import React from 'react';
import { Link } from 'react-router-dom';
import './CaseStudies.css';
import SEO from '../../components/SEO/SEO';


const caseStudiesData = [
    {
        id: 1,
        title: 'E-Commerce Growth Strategy',
        client: 'Fashion Retail Brand',
        industry: 'E-Commerce',
        challenge: 'The client was struggling with low online visibility and poor conversion rates despite having a great product line.',
        solution: 'We implemented a comprehensive digital strategy combining SEO optimization, Google Shopping campaigns, and conversion rate optimization.',
        results: [
            '300% increase in online revenue',
            '250% growth in organic traffic',
            '180% improvement in conversion rate',
            '400% ROI on ad spend'
        ],
        tags: ['SEO', 'Google Ads', 'CRO'],
        duration: '6 months',
        color: 'gradient-1'
    },
    {
        id: 2,
        title: 'B2B Lead Generation Campaign',
        client: 'SaaS Technology Company',
        industry: 'Software/SaaS',
        challenge: 'The company needed to generate high-quality B2B leads to scale their business but was struggling with targeting and messaging.',
        solution: 'We created a multi-channel lead generation strategy using LinkedIn advertising, content marketing, and marketing automation.',
        results: [
            '500+ qualified leads per month',
            '65% reduction in cost per lead',
            '40% increase in demo bookings',
            '25% improvement in lead-to-customer conversion'
        ],
        tags: ['LinkedIn Ads', 'Content Marketing', 'Lead Gen'],
        duration: '8 months',
        color: 'gradient-2'
    },
    {
        id: 3,
        title: 'Local SEO Domination',
        client: 'Multi-Location Restaurant Chain',
        industry: 'Food & Beverage',
        challenge: 'Poor local search visibility was resulting in lost foot traffic to competitors, especially for mobile searches.',
        solution: 'We optimized their Google My Business profiles, built local citations, and created location-specific content strategies.',
        results: [
            '450% increase in local search visibility',
            '200% more phone calls from Google',
            '300% increase in direction requests',
            'Top 3 rankings for 85% of target keywords'
        ],
        tags: ['Local SEO', 'GMB', 'Content Strategy'],
        duration: '5 months',
        color: 'gradient-3'
    },
    {
        id: 4,
        title: 'Social Media Brand Building',
        client: 'Luxury Lifestyle Brand',
        industry: 'Retail/Lifestyle',
        challenge: 'The brand had minimal social media presence and was losing market share to more digitally-savvy competitors.',
        solution: 'We developed a comprehensive social media strategy with high-quality content, influencer partnerships, and targeted advertising.',
        results: [
            '1000% growth in Instagram followers',
            '500% increase in engagement rate',
            '250% boost in website traffic from social',
            '$2M in attributed social commerce revenue'
        ],
        tags: ['Social Media', 'Influencer Marketing', 'Content Creation'],
        duration: '12 months',
        color: 'gradient-1'
    },
    {
        id: 5,
        title: 'Website Redesign & Optimization',
        client: 'Professional Services Firm',
        industry: 'B2B Services',
        challenge: 'The company\'s outdated website was creating a poor first impression and failing to generate leads.',
        solution: 'We completely redesigned and rebuilt their website with modern design, optimized user experience, and conversion-focused architecture.',
        results: [
            '400% increase in contact form submissions',
            '85% reduction in bounce rate',
            '200% improvement in average session duration',
            '150% growth in organic search traffic'
        ],
        tags: ['Web Design', 'UX/UI', 'Conversion Optimization'],
        duration: '4 months',
        color: 'gradient-2'
    },
    {
        id: 6,
        title: 'Email Marketing Automation',
        client: 'Online Education Platform',
        industry: 'EdTech',
        challenge: 'Low student engagement and poor course completion rates were impacting revenue and brand reputation.',
        solution: 'We implemented a sophisticated email automation strategy with personalized nurture sequences and behavioral triggers.',
        results: [
            '350% increase in email engagement',
            '45% improvement in course completion',
            '$500K in attributed revenue from email',
            '60% reduction in student churn'
        ],
        tags: ['Email Marketing', 'Marketing Automation', 'Personalization'],
        duration: '6 months',
        color: 'gradient-3'
    }
];

const CaseStudies = () => {
    return (
        <div className="case-studies-page">
            <SEO
                title="Case Studies & Portfolio"
                description="View our portfolio of successful digital marketing campaigns. See how Digi Network has helped businesses grow with SEO, Ads, and Web Design."
                canonical="/case-studies"
            />
            {/* Hero Section */}
            <section className="case-studies-hero">
                <div className="case-studies-hero-bg"></div>
                <div className="container">
                    <div className="case-studies-hero-content">
                        <div className="label">CASE STUDIES & PORTFOLIO</div>
                        <h1>Real Results for Real Businesses</h1>
                        <p>Discover how we've helped businesses achieve remarkable growth through strategic SEO, high-converting paid advertising, and stunning web development. Each case study showcases our data-driven approach and commitment to delivering measurable ROI.</p>
                        <Link to="/contact" className="btn btn-primary">
                            View All Projects
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Case Studies Grid */}
            <section className="case-studies-grid-section">
                <div className="container">
                    <div className="case-studies-grid">
                        {caseStudiesData.map((study) => (
                            <div className={`case-study-card ${study.color}`} key={study.id}>
                                {/* IMAGE PLACEHOLDER: Add client logo or dashboard screenshot here (recommended size: 100% width, 300px height) */}
                                <div className="case-study-image">
                                    <div className="case-study-image-placeholder">
                                        <p>📊 Add Client Logo or Screenshot</p>
                                    </div>
                                </div>
                                <div className="case-study-header">
                                    <h3>{study.title}</h3>
                                    <div className="case-study-meta">
                                        <span className="case-study-client">{study.client}</span>
                                        <span className="case-study-industry">{study.industry}</span>
                                    </div>
                                </div>

                                <div className="case-study-content">
                                    <div className="case-study-section">
                                        <h4>Challenge</h4>
                                        <p>{study.challenge}</p>
                                    </div>

                                    <div className="case-study-section">
                                        <h4>Solution</h4>
                                        <p>{study.solution}</p>
                                    </div>

                                    <div className="case-study-section">
                                        <h4>Results</h4>
                                        <ul className="case-study-results">
                                            {study.results.map((result, idx) => (
                                                <li key={idx}>{result}</li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="case-study-footer">
                                        <div className="case-study-tags">
                                            {study.tags.map((tag, idx) => (
                                                <span key={idx} className="case-study-tag">{tag}</span>
                                            ))}
                                        </div>
                                        <div className="case-study-duration">
                                            <span>Duration: {study.duration}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="case-studies-cta">
                <div className="container">
                    <h2>Ready to Be Our Next Success Story?</h2>
                    <p>Let's create amazing results for your business</p>
                    <Link to="/contact" className="btn btn-primary">
                        Start Your Project
                        <svg width="25" height="23" viewBox="0 0 25 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M24.4766 12.1562L14.8516 21.7812C14.5234 22.1094 13.9219 22.1094 13.5938 21.7812C13.2656 21.4531 13.2656 20.8516 13.5938 20.5234L21.7422 12.375H1.125C0.632812 12.375 0.25 11.9922 0.25 11.5C0.25 11.0625 0.632812 10.625 1.125 10.625H21.7422L13.5938 2.53125C13.2656 2.20312 13.2656 1.60156 13.5938 1.27344C13.9219 0.945312 14.5234 0.945312 14.8516 1.27344L24.4766 10.8984C24.8047 11.2266 24.8047 11.8281 24.4766 12.1562Z" fill="white" />
                        </svg>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default CaseStudies;
